import { Button, DataTable, PageHead } from "@nabble/ui";
import { useMemo, useState } from "react";
import type { AdminUser } from "../../../api/adminUsers";
import { ShareLinkDialog, type UnsentLink } from "../../../components/ShareLinkDialog";
import { AnonymizeUserDialog } from "./AnonymizeUserDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { PendingUserActionDialogs } from "./PendingUserActionDialogs";
import { useAdminUsers } from "./useAdminUsers";
import { usePendingUserAction } from "./usePendingUserAction";
import { UserAccessDialog } from "./UserAccessDialog";
import { userActions } from "./userActions";
import { userColumns } from "./userColumns";
import { UserMembershipsDialog } from "./UserMembershipsDialog";

export default function AdminUsersPage(): React.JSX.Element {
  const { users, roles, organizations, loading, loadError, reload } = useAdminUsers();
  const [createOpen, setCreateOpen] = useState(false);
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null);
  const [membershipsUser, setMembershipsUser] = useState<AdminUser | null>(null);
  const [anonymizeTarget, setAnonymizeTarget] = useState<AdminUser | null>(null);
  const [unsentLink, setUnsentLink] = useState<UnsentLink | null>(null);
  const pendingAction = usePendingUserAction(reload, setUnsentLink);
  const { confirm } = pendingAction;

  const columns = useMemo(
    () =>
      userColumns({
        organizations,
        actions: (u) => userActions(u, { manageMemberships: setMembershipsUser, editAccess: setAccessUser, confirm, anonymize: setAnonymizeTarget }),
      }),
    [organizations, confirm],
  );

  function closeAndReload(close: () => void): () => void {
    return () => {
      close();
      reload();
    };
  }

  return (
    <div>
      <PageHead titleHidden title="Users" actions={<Button label="Invite user" icon="userPlus" onClick={() => setCreateOpen(true)} />} />

      <DataTable<AdminUser>
        id="admin-users"
        rowKey="id"
        loading={loading}
        rows={users}
        columns={columns}
        exportName="users"
        searchPlaceholder="Search by name or email"
        defaultSort={{ key: "displayName", dir: "asc" }}
        error={loadError ? { title: "We couldn't load the list", description: loadError, action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} /> } : null}
        empty={{
          icon: "users",
          title: "No users yet",
          description: "Invite people to your organizations. They'll get an email to create their password.",
          action: <Button label="Invite user" icon="userPlus" onClick={() => setCreateOpen(true)} />,
        }}
      />

      {createOpen ? (
        <CreateUserDialog
          roles={roles}
          organizations={organizations}
          onClose={() => setCreateOpen(false)}
          onCreated={(email, link) => {
            setCreateOpen(false);
            if (link) setUnsentLink({ link, email });
            reload();
          }}
        />
      ) : null}

      {accessUser ? (
        <UserAccessDialog user={accessUser} onClose={() => setAccessUser(null)} onSaved={closeAndReload(() => setAccessUser(null))} />
      ) : null}

      {membershipsUser ? (
        <UserMembershipsDialog
          user={membershipsUser}
          organizations={organizations}
          roles={roles}
          onClose={() => setMembershipsUser(null)}
          onSaved={closeAndReload(() => setMembershipsUser(null))}
        />
      ) : null}

      {anonymizeTarget ? (
        <AnonymizeUserDialog user={anonymizeTarget} onClose={() => setAnonymizeTarget(null)} onAnonymized={closeAndReload(() => setAnonymizeTarget(null))} />
      ) : null}

      <PendingUserActionDialogs state={pendingAction} />

      {unsentLink ? <ShareLinkDialog unsent={unsentLink} onClose={() => setUnsentLink(null)} /> : null}
    </div>
  );
}
